"use client";

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Building2, CheckCircle, AlertTriangle } from "lucide-react";
import { BankService } from "@/domain/bank";
import { useCurrency } from "@/stores/currency-store";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { findBank } from "@/data/banks";
import type { WebBankAccount } from "@/domain/bank/bank.types";

interface ApproveBankDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: "manual" | "auto";
  currentBankId?: string | null;
  onApprove: (bankId: string, reason: string) => Promise<void>;
}

export function ApproveBankDialog({
  open,
  onOpenChange,
  type,
  currentBankId,
  onApprove,
}: ApproveBankDialogProps) {
  const { format: formatCurrency } = useCurrency();
  const [selectedBankId, setSelectedBankId] = useState<string>("");
  const [reason, setReason] = useState("");
  const [banks, setBanks] = useState<WebBankAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  // ✅ เพิ่ม state สำหรับ confirm dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingApproval, setPendingApproval] = useState<{
    bankId: string;
    reason: string;
  } | null>(null);

  // Load banks
  useEffect(() => {
    if (!open) return;

    const loadBanks = async () => {
      setIsLoading(true);
      try {
        const token =
          sessionStorage.getItem("admin_session_token") ||
          localStorage.getItem("admin_session_token");
        if (!token) {
          toast.error("กรุณาเข้าสู่ระบบใหม่");
          return;
        }

        // ✅ ดึงบัญชีถอนทั้งหมด (withdraw + both) - แสดงทั้งหมด ไม่กรอง gatewayApi
        const allBanks = await BankService.getWebBankAccounts(token);

        // กรองเฉพาะบัญชีที่มีความสามารถถอน (withdraw หรือ both)
        const withdrawBanks = allBanks.filter((b) => {
          const canWithdraw = b.type === "withdraw" || b.isBoth === true;
          return canWithdraw;
        });

        // รวมกันและเรียงตาม seq
        const sortedBanks = withdrawBanks.sort(
          (a, b) => (a.seq || 0) - (b.seq || 0)
        );

        setBanks(sortedBanks);

        // ตั้งค่า bank เริ่มต้น
        // 1. ถ้ามี currentBankId → ใช้ currentBankId
        // 2. ถ้าไม่มี → หา primaryWithdraw = true
        // 3. ถ้าไม่มี → ใช้ตัวแรก
        if (currentBankId && sortedBanks.find((b) => b.id === currentBankId)) {
          setSelectedBankId(currentBankId);
        } else {
          const primaryWithdrawBank = sortedBanks.find(
            (b) => b.primaryWithdraw
          );
          if (primaryWithdrawBank) {
            setSelectedBankId(primaryWithdrawBank.id);
          } else if (sortedBanks.length > 0) {
            setSelectedBankId(sortedBanks[0].id);
          }
        }
      } catch (error) {
        console.error("Error loading banks:", error);
        toast.error("ไม่สามารถโหลดข้อมูลบัญชีธนาคาร");
      } finally {
        setIsLoading(false);
      }
    };

    loadBanks();
  }, [open, currentBankId]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setReason("");
      setError("");
      setSelectedBankId("");
      setShowConfirmDialog(false);
      setPendingApproval(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!selectedBankId) {
      setError("กรุณาเลือกบัญชีธนาคาร");
      return;
    }

    // ✅ แสดง confirm dialog ก่อนอนุมัติ (หลังจากเลือกบัญชีแล้ว)
    setPendingApproval({
      bankId: selectedBankId,
      reason: reason.trim(),
    });
    setShowConfirmDialog(true);
  };

  // ✅ Handler สำหรับ confirm dialog
  const handleConfirmApproval = async () => {
    if (!pendingApproval) return;

    setIsSubmitting(true);
    setError("");

    try {
      await onApprove(pendingApproval.bankId, pendingApproval.reason);
      setReason("");
      setSelectedBankId("");
      setPendingApproval(null);
      setShowConfirmDialog(false);
      onOpenChange(false);
    } catch (err) {
      setError((err as Error).message);
      setShowConfirmDialog(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setReason("");
      setError("");
      setSelectedBankId("");
      onOpenChange(false);
    }
  };

  const selectedBank = banks.find((b) => b.id === selectedBankId);

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              <span>
                {type === "manual" ? "อนุมัติ Manual" : "อนุมัติ Auto"}
              </span>
            </DialogTitle>
            <DialogDescription>
              {type === "manual"
                ? "เลือกบัญชีธนาคารที่จะใช้ถอนเงิน (Manual)"
                : "เลือกบัญชีธนาคารที่จะใช้ถอนเงิน (Auto - ส่ง payment gateway)"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 flex-1 overflow-y-auto">
            {/* เลือกบัญชีธนาคาร */}
            <div className="space-y-3">
              <Label className="font-medium text-base">
                เลือกบัญชีธนาคาร <span className="text-red-500">*</span>
              </Label>

              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">
                    กำลังโหลดข้อมูลบัญชี...
                  </span>
                </div>
              ) : banks.length === 0 ? (
                <div className="p-4 rounded-lg bg-muted/50 text-center">
                  <p className="text-sm text-muted-foreground">
                    ไม่พบบัญชีธนาคารสำหรับถอนเงิน
                  </p>
                </div>
              ) : (
                <ScrollArea className="h-[300px] pr-4">
                  <div className="space-y-2">
                    {banks.map((bank) => {
                      const isSelected = selectedBankId === bank.id;
                      const bankInfo = findBank(bank.bankName);
                      const bankImage =
                        bankInfo?.image ||
                        bank.imageUrl ||
                        "/images/banks/default.png";

                      return (
                        <button
                          key={bank.id}
                          type="button"
                          onClick={() => {
                            setSelectedBankId(bank.id);
                            if (error) setError("");
                          }}
                          className={cn(
                            "w-full p-4 rounded-lg border-2 transition-all text-left",
                            "hover:bg-accent hover:border-primary/50",
                            isSelected
                              ? "border-primary bg-primary/5"
                              : "border-border bg-background"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={cn(
                                "w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                                isSelected
                                  ? "border-primary bg-primary"
                                  : "border-muted-foreground"
                              )}
                            >
                              {isSelected && (
                                <CheckCircle className="w-3 h-3 text-primary-foreground" />
                              )}
                            </div>
                            {/* Bank Logo */}
                            <div className="flex-shrink-0">
                              <img
                                src={bankImage}
                                alt={bank.bankName}
                                className="w-10 h-10 object-contain rounded"
                                onError={(e) => {
                                  // Fallback to default image
                                  const target = e.target as HTMLImageElement;
                                  target.src = "/images/banks/default.png";
                                }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="font-medium text-sm">
                                  {bank.name}
                                </div>
                                {bank.primaryWithdraw && (
                                  <Badge
                                    variant="outline"
                                    className="text-[10px] px-1.5 py-0"
                                  >
                                    Default
                                  </Badge>
                                )}
                                {bank.gatewayApi && (
                                  <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30 text-[10px] px-1.5 py-0">
                                    Gateway API
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {bank.bankName} - {bank.bankAccount}
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="text-sm font-medium">
                                {formatCurrency(bank.amount)}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                ยอดคงเหลือ
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}

              {error && !selectedBankId && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {error}
                </p>
              )}
            </div>

            {/* แสดงข้อมูลบัญชีที่เลือก */}
            {selectedBank && (
              <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                <div className="flex items-center gap-3">
                  {/* Bank Logo */}
                  {(() => {
                    const bankInfo = findBank(selectedBank.bankName);
                    const bankImage =
                      bankInfo?.image ||
                      selectedBank.imageUrl ||
                      "/images/banks/default.png";
                    return (
                      <img
                        src={bankImage}
                        alt={selectedBank.bankName}
                        className="w-12 h-12 object-contain rounded flex-shrink-0"
                        onError={(e) => {
                          // Fallback to default
                          const target = e.target as HTMLImageElement;
                          target.src = "/images/banks/default.png";
                        }}
                      />
                    );
                  })()}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <div className="font-medium text-sm">
                        {selectedBank.name}
                      </div>
                      {selectedBank.primaryWithdraw && (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0"
                        >
                          Default
                        </Badge>
                      )}
                      {selectedBank.gatewayApi && (
                        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30 text-[10px] px-1.5 py-0">
                          Gateway API
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {selectedBank.bankName} - {selectedBank.bankAccount}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs flex-shrink-0">
                    {formatCurrency(selectedBank.amount)}
                  </Badge>
                </div>
              </div>
            )}

            {/* เหตุผล (ไม่บังคับ) */}
            <div className="space-y-2">
              <Label htmlFor="reason" className="font-medium">
                เหตุผล (ไม่บังคับ)
              </Label>
              <Textarea
                id="reason"
                placeholder="ระบุเหตุผลในการอนุมัติ..."
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  if (error) setError("");
                }}
                disabled={isSubmitting}
                rows={3}
                className={cn(error && "border-red-500")}
              />
              {error && selectedBankId && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {error}
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              ยกเลิก
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!selectedBankId || isSubmitting}
              className={cn(
                type === "manual"
                  ? "bg-success hover:bg-success/90"
                  : "bg-warning hover:bg-warning/90"
              )}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  กำลังดำเนินการ...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  ยืนยันอนุมัติ
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ✅ Confirm Dialog - แสดงหลังจากเลือกบัญชีแล้ว */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ยืนยันอนุมัติ{type === "manual" ? " Manual" : " Auto"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              ต้องการอนุมัติรายการนี้
              {type === "manual" ? "แบบ Manual" : "แบบ Auto"}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>
              ยกเลิก
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmApproval}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  กำลังดำเนินการ...
                </>
              ) : (
                "ยืนยัน"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
